import { Component, ViewEncapsulation, input, inject, computed, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApContentService } from '@ap/content';
import '@asn/icon';

import content from './checkbox.content.json';

/**
 * This is the Checkbox Component. Renders a checkbox with an icon instead of the native browser styling. Use this instead of a
 * native checkbox (`<input type="checkbox">`).
 **/
@Component({
    selector: 'ap-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApFormErrorsComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ApCheckboxComponent implements ControlValueAccessor {
    protected control = inject(NgControl, { self: true, optional: true })!;
    protected readonly contentService = inject(ApContentService);

    /**
     * Description of checkbox. Shown above the checkbox
     */
    public readonly description = input<string>();

    /**
     * The checkbox label
     */
    public readonly label = input<string>();

    /**
     * Subtitle of checkbox. Shown below content
     */
    public readonly subtitle = input<string>();

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>();

    /**
     * Id of element that describes radio button
     */
    readonly ariaDescribedby = input<string>('');

    protected readonly computedAriaDescribedby = computed(() => {
        const ids = [
            ...(this.ariaDescribedby() ? [this.ariaDescribedby()] : []),
            ...(this.description() ? [`${this.uniqueId}-description`] : []),
            ...(this.control ? [`${this.uniqueId}-errors`] : [])
        ].join(' ');
        return ids.length > 0 ? ids : null;
    });

    public isDisabled = false;
    public isChecked = false;

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `checkbox-${Math.round(Math.random() * 10000000)}`;

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = () => {};

    constructor() {
        this.control && (this.control.valueAccessor = this);
        this.contentService.addContentItems(content);
        this.content = content;
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(checked: boolean) {
        this.isChecked = checked;
    }

    /**
     * change the isChecked status
     * @param event
     */
    protected onCheckboxChange(event: Event) {
        const isChecked = (event.target as HTMLInputElement).checked;
        this.isChecked = isChecked;
        this.onChange(isChecked);
        this.onTouched();
    }
}
